import React, { useState } from 'react';
import { Shield, Lock, User, Eye, EyeOff, ArrowLeft, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import LogoIcon from '../../assets/logo.svg';

const AdminLoginPage = ({ onLoginSuccess }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!username.trim() || !password.trim()) {
            return toast.error("Por favor, preencha o usuário e a senha.");
        }

        setLoading(true);
        const loadToast = toast.loading("Verificando credenciais de equipe...");

        try {
            const response = await api.post('/auth/admin/login', {
                username: username.trim(),
                password: password.trim()
            });

            if (response.data.success) {
                localStorage.setItem('is_logged_in', 'true');
                toast.success(`Bem-vindo ao Painel de Gestão, ${response.data.user.name || response.data.user.username}!`, { id: loadToast });
                if (onLoginSuccess) {
                    onLoginSuccess(response.data.user);
                }
            }
        } catch (err) {
            const msg = err.response?.data?.error || "Erro ao conectar com o servidor administrativo.";
            setErrorMessage(msg);
            toast.error(msg, { id: loadToast });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#090d16',
            backgroundImage: 'radial-gradient(ellipse 80% 80% at 50% -20%, rgba(147, 51, 234, 0.15), rgba(255, 255, 255, 0))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: 'Outfit, sans-serif'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '440px',
                backgroundColor: '#0f172a',
                border: '1px solid #1e293b',
                borderRadius: '24px',
                padding: '40px 32px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(147, 51, 234, 0.1)',
                position: 'relative'
            }}>
                {/* Header / Logo */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '64px',
                        height: '64px',
                        borderRadius: '20px',
                        backgroundColor: 'rgba(147, 51, 234, 0.12)',
                        border: '1px solid rgba(147, 51, 234, 0.3)',
                        marginBottom: '16px'
                    }}>
                        <Shield size={32} color="#a855f7" />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '6px' }}>
                        <img src={LogoIcon} alt="Logo" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                        <span style={{ color: '#fff', fontSize: '18px', fontWeight: '800', letterSpacing: '-0.02em' }}>
                            Anúncios App
                        </span>
                        <span style={{
                            backgroundColor: '#9333ea',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: '800',
                            padding: '2px 8px',
                            borderRadius: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Admin
                        </span>
                    </div>

                    <h2 style={{ color: '#f8fafc', fontSize: '22px', fontWeight: '700', margin: '8px 0 4px 0' }}>
                        Acesso Restrito
                    </h2>
                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
                        Autenticação exclusiva para equipe de Administração, Moderação e Suporte
                    </p>
                </div>

                {/* Banner de Erro caso ocorra */}
                {errorMessage && (
                    <div style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.12)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#fca5a5',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <Lock size={16} color="#ef4444" style={{ flexShrink: 0 }} />
                        <span>{errorMessage}</span>
                    </div>
                )}

                {/* Formulário de Login */}
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '18px' }}>
                        <label style={{
                            display: 'block',
                            color: '#cbd5e1',
                            fontSize: '13px',
                            fontWeight: '600',
                            marginBottom: '8px'
                        }}>
                            Usuário do Administrador
                        </label>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <User size={18} color="#64748b" style={{ position: 'absolute', left: '14px' }} />
                            <input
                                type="text"
                                placeholder="Digite seu usuário ou email"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    backgroundColor: '#090d16',
                                    border: '1px solid #334155',
                                    borderRadius: '12px',
                                    padding: '12px 14px 12px 42px',
                                    color: '#fff',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'border 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#a855f7'}
                                onBlur={(e) => e.target.style.borderColor = '#334155'}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <label style={{
                            display: 'block',
                            color: '#cbd5e1',
                            fontSize: '13px',
                            fontWeight: '600',
                            marginBottom: '8px'
                        }}>
                            Senha de Acesso
                        </label>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <KeyRound size={18} color="#64748b" style={{ position: 'absolute', left: '14px' }} />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    backgroundColor: '#090d16',
                                    border: '1px solid #334155',
                                    borderRadius: '12px',
                                    padding: '12px 42px 12px 42px',
                                    color: '#fff',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'border 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#a855f7'}
                                onBlur={(e) => e.target.style.borderColor = '#334155'}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    background: 'none',
                                    border: 'none',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: 0
                                }}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            backgroundColor: '#9333ea',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '14px',
                            fontSize: '15px',
                            fontWeight: '700',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background 0.2s, transform 0.1s',
                            boxShadow: '0 4px 14px 0 rgba(147, 51, 234, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#7e22ce'; }}
                        onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#9333ea'; }}
                    >
                        {loading ? (
                            <span>Autenticando...</span>
                        ) : (
                            <>
                                <Shield size={18} />
                                <span>Entrar no Painel Admin</span>
                            </>
                        )}
                    </button>
                </form>

                {/* Footer link to main app */}
                <div style={{ marginTop: '28px', textAlign: 'center', borderTop: '1px solid #1e293b', paddingTop: '20px' }}>
                    <button
                        type="button"
                        onClick={() => window.location.href = '/'}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#cbd5e1'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                    >
                        <ArrowLeft size={14} /> Voltar para o portal de afiliados
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;
